import { StrictMode, useEffect } from 'react'

import 'core-js/proposals/promise-with-resolvers'
import { ExtraErrorData } from '@sentry/integrations'
import { init, reactRouterV6Instrumentation, setTag, withProfiler } from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import ReactDOM from 'react-dom/client'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

import '@acre/design-system/mui-x-license'
import acreConfig from '@acre/config'
import { setDesignSystemConfig } from '@acre/design-system'
import type { LutherException } from '@acre/graphql'
import { enrichHttpSpansWithMicroserviceTags } from '@acre/graphql'
import { getTeamComponentFromPath, preloadCsClarityFont, setUtilsConfig } from '@acre/utils'

import App from './App'
import AppProviders from './AppProviders'
import AppSuspenseWrapper from './AppSuspenseWrapper'

// `@acre/utils` imports no environment module of its own, so the application injects the
// environment specific values it needs before anything renders or issues a request.
setUtilsConfig({
  generalInsuranceRsaPublicKeyUrl: acreConfig.GENERAL_INSURANCE_RSA_PUBLIC_KEY_URL,
  protectionRsaPublicKeyUrl: acreConfig.PROTECTION_RSA_PUBLIC_KEY_URL,
  applicationRsaPublicKeyUrl: acreConfig.APPLICATION_RSA_PUBLIC_KEY_URL,
  refreshUrl: acreConfig.REFRESH_URL,
  clientOriginator: acreConfig.CLIENT_ORIGINATOR,
  globalFeatureFlags: acreConfig.GLOBAL_FEATURE_FLAGS,
})

// `@acre/design-system` imports no environment module of its own, so the application injects
// the environment specific values it needs before anything renders or issues a request.
setDesignSystemConfig({
  apiUrl: acreConfig.API_URL,
  mixpanelToken: acreConfig.MIXPANEL_TOKEN,
  sentryEnvironment: acreConfig.SENTRY_ENVIRONMENT,
  matomoBaseUrl: acreConfig.MATOMO_BASE_URL,
  matomoSiteId: acreConfig.MATOMO_SITE_ID,
})

const isPlaywright = navigator.webdriver
// @ts-expect-error - Cypress is a global variable
const isCypress = window.Cypress

// Disable sentry reporting in test environments
if (import.meta.env.MODE === 'production' && !isCypress && !isPlaywright) {
  init({
    dsn: 'https://ee7d1ac2845d4d9a998f53da3575f68a@sentry.io/1876005',
    environment: acreConfig.SENTRY_ENVIRONMENT,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: reactRouterV6Instrumentation(
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
      new ExtraErrorData({ depth: 5 }),
    ],
    tracesSampler: (samplingContext) => {
      if (samplingContext?.transactionContext?.op === 'navigation') return 0.014 // 1.4%
      if (samplingContext?.transactionContext?.name === 'search-company') return 0.009 // 0.9%

      // Apply a 0.1% sample rate for all other transactions (10× lower)
      return 0.001
    },
    beforeSendTransaction: enrichHttpSpansWithMicroserviceTags,
    beforeSend(event, hint) {
      // This ensures that sentry is grouping our events as we want them, rather than putting all failing API calls together
      const error = hint?.originalException as Error

      // Ignore ApolloErrors
      if (error?.name === 'ApolloError') return null

      const hasGraphqlError =
        event.breadcrumbs?.some(
          (b) => b.category === 'sentry.event' && b.message && /GraphqlError:/i.test(b.message),
        ) || false

      // Skip logging GraphQL errors here, as they're already handled by graphqlErrorLink.
      if (hasGraphqlError) return null

      if (error && typeof error !== 'string' && error.name === 'LutherException') {
        const err = error as LutherException

        event.fingerprint = [err.message]
      }

      // Deducing the team and component from the URL and adding them to the tags list
      const teamTags = getTeamComponentFromPath(window.location.pathname, 'crm')
      event.tags = {
        ...event.tags,
        team: teamTags.team,
        component: teamTags.component,
      }

      return event
    },
  })

  setTag('issue_type', 'frontend')
  setTag('app', 'broker-crm')
}

// Allows us to use the sentry profiler, can track render times for the app component and descendants
const AppWithProfiler = withProfiler(AppSuspenseWrapper)

preloadCsClarityFont()

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <AppProviders>
      <AppWithProfiler>
        <App />
      </AppWithProfiler>
    </AppProviders>
  </StrictMode>,
)
